import { useState } from "react";
import {
  deviationCategoriesText,
  PRESCRIPTION_DEVIATION_CATEGORIES,
  PRESCRIPTION_DEVIATION_CATEGORY_LABEL,
  PRESCRIPTION_DEVIATION_OTHER_MAX,
  PRESCRIPTION_EVIDENCE_MATCH_LABEL,
  PRESCRIPTION_EVIDENCE_MATCHES,
  PRESCRIPTION_EVIDENCE_STATUS_LABEL,
  PRESCRIPTION_EVIDENCE_STATUSES,
  type PrescriptionEvidenceReview,
  type TransferReview,
} from "@care-by-ella/contracts";
import {
  reviewPrescriptionEvidence,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  draftOf,
  EVIDENCE_STATUS_TONE,
  evidenceDraftPayload,
  evidenceDraftProblem,
  toggleCategory,
  type EvidenceDraft,
} from "@/lib/prescription-evidence";
import { StatusTag } from "./StatusTag";
import { useReadOnly } from "./SupportView";

// TES-1057 (Emelie, point G) + TES-1049 (Ö-281) — beside each prescription
// or order document in "Bilagor från patienten": its review status, and
// whether it matches what she reported. Choosing that it deviates:
// "Uppgifterna avviker från receptunderlaget"
// opens the categories, at least one, with a required
// specification for "annat"; saving it raises the risk flag
// VERIFIERAD_AVVIKELSE_MOT_RECEPTUNDERLAG in Riskflaggor. One entry point for
// both issues. Hidden controls in the admin's read-only view (TES-1040): the
// status is still shown.

function dateSv(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Stockholm",
  });
}

export function PrescriptionEvidenceReviewControl({
  patientId,
  review,
  canEdit,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  review: PrescriptionEvidenceReview;
  // Only while the transfer review is open.
  canEdit: boolean;
  onSaved: (review: TransferReview) => void;
  onUnauthorized: () => void;
}) {
  const readOnly = useReadOnly();
  const [draft, setDraft] = useState<EvidenceDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idBase = `evidence-${review.uploadId}`;
  const problem = draft ? evidenceDraftProblem(draft) : null;

  async function save() {
    if (!draft || problem) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await reviewPrescriptionEvidence(
        patientId,
        review.uploadId,
        evidenceDraftPayload(draft),
      );
      setDraft(null);
      onSaved(updated);
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError("Granskningen kunde inte sparas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-xs flex flex-col gap-xs text-sm"
      data-testid="prescription-evidence-review"
    >
      <div className="flex flex-wrap items-center gap-sm">
        <span className="text-xs text-textSecondary">Receptunderlag:</span>
        <StatusTag tone={EVIDENCE_STATUS_TONE[review.status]}>
          {PRESCRIPTION_EVIDENCE_STATUS_LABEL[review.status]}
        </StatusTag>
        {review.matchesPatientData === "deviates" ? (
          <StatusTag tone="risk">
            {PRESCRIPTION_EVIDENCE_MATCH_LABEL.deviates}
          </StatusTag>
        ) : review.matchesPatientData === "matches" ? (
          <span className="text-xs">
            {PRESCRIPTION_EVIDENCE_MATCH_LABEL.matches}
          </span>
        ) : null}
      </div>
      {review.matchesPatientData === "deviates" ? (
        <p className="text-xs">
          Avviker:{" "}
          {deviationCategoriesText(
            review.deviationCategories,
            review.otherSpecification,
          )}
        </p>
      ) : null}
      {review.reviewedAt ? (
        <p className="text-xs text-textSecondary">
          Granskat av {review.reviewedBy?.name ?? "läkare"} ·{" "}
          {dateSv(review.reviewedAt)}
        </p>
      ) : null}

      {canEdit && !readOnly && !draft ? (
        <button
          type="button"
          onClick={() => setDraft(draftOf(review))}
          className="self-start text-xs text-brand underline"
        >
          {review.status === "not_reviewed"
            ? "Granska underlaget"
            : "Ändra granskningen"}
        </button>
      ) : null}

      {canEdit && !readOnly && draft ? (
        <div className="flex flex-col gap-sm rounded-md bg-surfaceMuted/40 p-sm">
          <fieldset className="flex flex-col gap-xs">
            <legend className="text-xs font-semibold text-textSecondary">
              Granskningsstatus
            </legend>
            {PRESCRIPTION_EVIDENCE_STATUSES.map((s) => (
              <label key={s} className="flex items-center gap-sm">
                <input
                  type="radio"
                  name={`${idBase}-status`}
                  checked={draft.status === s}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      status: s,
                      // Comparing is part of reviewing it.
                      match: s === "not_reviewed" ? null : draft.match,
                    })
                  }
                  disabled={busy}
                />
                {PRESCRIPTION_EVIDENCE_STATUS_LABEL[s]}
              </label>
            ))}
          </fieldset>
          {draft.status === "completion_required" ? (
            <p className="text-xs text-textSecondary">
              Statusen sparas på underlaget. Be patienten om kompletteringen med
              ”Begär komplettering” i beslutet.
            </p>
          ) : null}

          {draft.status !== "not_reviewed" ? (
            <fieldset className="flex flex-col gap-xs">
              <legend className="text-xs font-semibold text-textSecondary">
                Jämfört med patientens uppgifter
              </legend>
              {PRESCRIPTION_EVIDENCE_MATCHES.map((m) => (
                <label key={m} className="flex items-center gap-sm">
                  <input
                    type="radio"
                    name={`${idBase}-match`}
                    checked={draft.match === m}
                    onChange={() => setDraft({ ...draft, match: m })}
                    disabled={busy}
                  />
                  {PRESCRIPTION_EVIDENCE_MATCH_LABEL[m]}
                </label>
              ))}
            </fieldset>
          ) : null}

          {draft.match === "deviates" ? (
            <fieldset className="flex flex-col gap-xs">
              <legend className="text-xs font-semibold text-textSecondary">
                Vad avviker? Välj minst en.
              </legend>
              {PRESCRIPTION_DEVIATION_CATEGORIES.map((c) => (
                <label key={c} className="flex items-center gap-sm">
                  <input
                    type="checkbox"
                    checked={draft.categories.includes(c)}
                    onChange={() =>
                      setDraft({
                        ...draft,
                        categories: toggleCategory(draft.categories, c),
                      })
                    }
                    disabled={busy}
                  />
                  {PRESCRIPTION_DEVIATION_CATEGORY_LABEL[c]}
                </label>
              ))}
              {draft.categories.includes("other") ? (
                <label className="flex flex-col gap-xs">
                  <span className="text-xs text-textSecondary">
                    Specifikation (krävs)
                  </span>
                  <textarea
                    value={draft.otherSpecification}
                    maxLength={PRESCRIPTION_DEVIATION_OTHER_MAX}
                    onChange={(e) =>
                      setDraft({ ...draft, otherSpecification: e.target.value })
                    }
                    className="min-h-[48px] rounded-md border border-surfaceMuted p-sm text-sm"
                    disabled={busy}
                  />
                </label>
              ) : null}
            </fieldset>
          ) : null}

          {problem ? (
            <p className="text-xs text-textSecondary">{problem}</p>
          ) : null}
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || problem !== null}
              className="rounded-md bg-textPrimary px-md py-xs text-sm text-surface disabled:opacity-50"
            >
              {busy ? "Sparar…" : "Spara granskningen"}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
              disabled={busy}
              className="rounded-md px-md py-xs text-sm text-textSecondary hover:bg-surfaceMuted"
            >
              Avbryt
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
